/**
 * クリア演出（TK-014、FR-014 / FR-015、`04-architecture.md` effects）。
 *
 * 順序: 最後の結合による内部の継ぎ目の消失（FR-021 の規則が `render` 経由で既に走っている）
 * → 外周枠のフェードアウト → `Clear !` の表示 → 保持 → フェードアウト → 演出完了
 * （`CLEARED`）→ `Next Stage` のフェードイン。
 *
 * 実際のフェードは CSS transition が行い、本モジュールはクラスの付け外しと時間管理だけを
 * 受け持つ（view はタイミングの正、CSS は見た目の正）。所要時間の定数は `view/constants.ts`
 * に集約されている（OI-010）。
 *
 * 演出対象の要素は shell（`main.ts`）が骨格として最初から作っておき、ここでは生成しない。
 * `Next Stage` の場所は骨格が確保済みのため、可視化してもレイアウトシフトは起きない
 * （FR-015）。DOM 全体には依存しないため、シーケンスはユニットテストで検証できる。
 */

import { CLEAR_FADE_MS, CLEAR_HOLD_MS, FRAME_FADE_MS, SEAM_FADE_MS } from './constants';

/** 演出が操作する要素の最小面。`HTMLElement` はこれを構造的に満たす。 */
export interface EffectTarget {
  readonly classList: {
    add(name: string): void;
    remove(name: string): void;
  };
  /** `HTMLElement.hidden`。lib.dom では `until-found` を含む union のため広めに取る。 */
  hidden: boolean | string;
}

/** クリア演出の依存。 */
export interface ClearEffectsDeps {
  /** 盤面要素。`board--cleared` の付与で外周枠と盤外に面した継ぎ目が消える。 */
  readonly board: EffectTarget;
  /** `Clear !` バナー。`hidden` で出し入れし、フェードはクラスで行う。 */
  readonly banner: EffectTarget;
  /** `Next Stage`（または最終ステージの代替導線）。無いページでは `null`。 */
  readonly next: EffectTarget | null;
  /**
   * 送客導線（TK-018、FR-018）。`Next Stage` と併せて演出完了時にフェードインする。
   * 設定で無効化されたページでは `null`。
   */
  readonly outbound?: EffectTarget | null;
  /**
   * 運営元の X アカウントへの導線（TK-070）。送客導線と同じく演出完了時にフェードインする。
   * 設定で無効化されたページでは `null`。
   */
  readonly social?: EffectTarget | null;
  /** 遅延実行。省略時は `setTimeout`。テストは手動スケジューラを注入する。 */
  readonly schedule?: (fn: () => void, ms: number) => void;
  /**
   * `prefers-reduced-motion` の状態。真ならフェードの待ち時間を 0 にする（NFR-006）。
   * `Clear !` の保持時間は動きではないため短縮しない。
   */
  readonly reducedMotion?: boolean;
}

/** クリア演出のハンドル。 */
export interface ClearEffects {
  /**
   * 演出本体（`CLEARING` の間）。resolve で `CLEARED` へ進む
   * （`controller/game.GameDeps.runClearEffects`）。
   */
  readonly run: () => Promise<void>;
  /** `Next Stage` のフェードイン（`CLEARED` で呼ぶ）。 */
  readonly showNext: () => void;
}

export function createClearEffects(deps: ClearEffectsDeps): ClearEffects {
  const schedule = deps.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
  const fade = (ms: number): number => (deps.reducedMotion === true ? 0 : ms);
  const wait = (ms: number): Promise<void> => new Promise((resolve) => schedule(resolve, ms));

  return {
    run: async () => {
      // 1. 最後の結合で内部の継ぎ目がフェード中（FR-021）。消え切るのを待つ。
      await wait(fade(SEAM_FADE_MS));

      // 2. 外周枠と盤外に面した辺の継ぎ目を消す。画像がここで無加工になる。
      deps.board.classList.add('board--cleared');
      await wait(fade(FRAME_FADE_MS));

      // 3. `Clear !` を画像上に重ねて表示し、読み終えるまで保持する。
      deps.banner.classList.remove('clear-banner--fading');
      deps.banner.hidden = false;
      await wait(CLEAR_HOLD_MS);

      // 4. `Clear !` をフェードアウトして片付ける。
      deps.banner.classList.add('clear-banner--fading');
      await wait(fade(CLEAR_FADE_MS));
      deps.banner.hidden = true;
    },
    showNext: () => {
      deps.next?.classList.add('next-stage--visible');
      deps.outbound?.classList.add('outbound-link--visible');
      deps.social?.classList.add('social-link--visible');
    },
  };
}
